import {UserAccountApi} from '@frontend/api/UserAccountApi'
import {appUrl} from '@frontend/AppUrl'
import {UserAuthApi} from '@frontend/modules/public/auth/UserAuthApi'
import type {UserData} from '@frontend/types'
import type {AppSection} from '@frontend/types/GlobalStores'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import {create} from 'zustand/react'
import {AppConfig} from '../AppConfig'
import {AnalyticsEventReporter} from '../helpers/AnalyticsEventReporter'
import {CacheService} from '../services/CacheService'
import {useAppRootStore} from './useAppRootStore'
import {useModalsStore} from './useModalsStore'

type OnRegistrationSuccessCallbackReturn = 'continue' | 'stop' | undefined

// Тип хранилища данных пользователя.
export interface AuthStoreType {
    // Данные:

    // Данные пользователя из API.
    user: UserData | null

    // Хеш данных пользователя для сравнения с новыми данными.
    _userHash: string | null

    // Состояние загрузки данных пользователя.
    loadingUserData: boolean

    // Функция, которую нужно вызвать после успешной авторизации или регистрации.
    onLoginSuccess: null | ((user: UserData) => void)

    // Функция, которую нужно вызвать после успешной регистрации.
    // Возвращает: 'continue' - продолжить стандартную обработку регистрации
    // (показать экран "Регистрация завершена"),
    // 'stop' или undefined - остановить стандартную обработку регистрации.
    onRegistrationSuccess: null | ((user: UserData) => OnRegistrationSuccessCallbackReturn)

    // Функция, которую нужно вызвать после загрузки данных пользователя (не зависит от результата).
    onUserDataLoaded: null | ((user: UserData | null) => void)

    // API:

    // Проверка наличия данных пользователя.
    isLoggedIn: () => boolean

    // Изменение состояния загрузки данных пользователя.
    setIsLoadingUserData: (enable: boolean) => void

    // Сохранение данных пользователя.
    setUserData: (user: null | UserData, removeLoadingState?: boolean) => void

    // Установить функцию, которая будет выполнена после успешной загрузки данных пользователя.
    // Если данные не загружены, то функция не будет вызвана никогда.
    setOnUserDataLoaded: (onUserDataLoaded: (user: UserData | null) => void) => void

    // Установить функцию, которая будет выполнена после успешной регистрации.
    setOnLoginSuccess: (onLoginSuccess?: null | (() => void)) => void

    // Установить функцию, которая будет выполнена после успешной регистрации.
    setOnRegistrationSuccess: (
        onRegistrationSuccess?: null | (() => OnRegistrationSuccessCallbackReturn)
    ) => void

    /*
     * Показать модальное окно авторизации, если пользователь не авторизован.
     * После успешной авторизации или регистрации будет вызван onLoginSuccess.
     */
    initUserAuthModal: (
        onLoginSuccess?: null | (() => void),
        onRegistrationSuccess?: null | (() => OnRegistrationSuccessCallbackReturn)
    ) => boolean

    // Настроить выполнение переданной функции после успешной авторизации.
    initLoginPage: (onLoginSuccess: (user: UserData) => void) => void

    // Отмена авторизации пользователя.
    cancelUserAuth: (navigatedToRegistration?: boolean) => void

    // Пользователь успешно авторизован.
    finishUserAuth: (userData: UserData, ignoreOnLoginSuccess?: boolean) => void

    // Обработка ошибки авторизации из API.
    onNotAuthorizedApiError: () => void

    // Перезагрузка данных пользователя.
    reloadUserData: (
        silent?: boolean,
        // Если данные есть в кеше и не просрочены, то использовать их.
        preferCached?: boolean,
        requestConfig?: RequestInit
    ) => Promise<UserData | null>

    // Выход из ЛК.
    logout: (section?: AppSection) => void
}

// Контейнер состояния данных пользователя.
export const useAuthStore = create<AuthStoreType>(
    (set, get) => ({
        user: null,
        _userHash: null,
        loadingUserData: false,
        onLoginSuccess: null,
        onRegistrationSuccess: null,
        onUserDataLoaded: null,

        // API:

        isLoggedIn(): boolean {
            return !!get().user
        },

        setIsLoadingUserData(enable: boolean): void {
            set({
                loadingUserData: enable,
            })
        },

        setUserData(user: null | UserData, removeLoadingState: boolean = true): void {
            const userHash = user ? JSON.stringify(user) : null
            if (userHash === get()._userHash) {
                // Нет изменений.
                if (removeLoadingState && get().loadingUserData) {
                    set({
                        loadingUserData: false,
                    })
                }
                // Не нужно менять ничего, поэтому останавливаемся и экономим перерисовки.
                return
            }
            if (removeLoadingState && get().loadingUserData) {
                set({
                    user,
                    _userHash: userHash,
                    loadingUserData: false,
                })
            } else {
                set({
                    user,
                    _userHash: userHash,
                })
            }
            // Отправляем ID пользователя в сервисы сбора аналитики.
            AnalyticsEventReporter.sendUserInfoToAnalytics(user)
        },

        setOnUserDataLoaded(onUserDataLoaded: AuthStoreType['onUserDataLoaded']): void {
            if (onUserDataLoaded && !get().loadingUserData) {
                // Данные пользователя уже загружены.
                onUserDataLoaded(get().user)
                return
            }
            set({
                onUserDataLoaded,
            })
        },

        setOnLoginSuccess(
            onLoginSuccess: null | (() => void) = null
        ): void {
            set({
                onLoginSuccess,
            })
        },

        setOnRegistrationSuccess(
            onRegistrationSuccess: null | (() => OnRegistrationSuccessCallbackReturn) = null
        ): void {
            set({
                onRegistrationSuccess,
            })
        },

        initUserAuthModal(
            onLoginSuccess: null | (() => void) = null,
            onRegistrationSuccess: null | (() => OnRegistrationSuccessCallbackReturn) = null
        ): boolean {
            const user = get().user
            if (!user) {
                useModalsStore.getState().showLoginModal()
                set({
                    onLoginSuccess,
                    onRegistrationSuccess,
                })
                return true
            }
            return false
        },

        initLoginPage(onLoginSuccess: (user: UserData) => void): void {
            const user = get().user
            if (user) {
                onLoginSuccess(user)
                return
            }
            set({
                onLoginSuccess,
            })
        },

        cancelUserAuth(navigatedToRegistration: boolean = false): void {
            useModalsStore.getState().hideLoginModal()
            if (navigatedToRegistration) {
                set({
                    onLoginSuccess: null,
                    onRegistrationSuccess: null,
                })
            }
        },

        finishUserAuth(userData: UserData, ignoreOnLoginSuccess: boolean = false): void {
            // Сохраняем данные пользователя
            get().setUserData(userData, true)

            // Скрываем окно авторизации.
            useModalsStore.getState().hideLoginModal()

            if (!ignoreOnLoginSuccess) {
                // Выносим в переменную т.к. authCallbacksStore.onLoginSuccess = null
                // будет вызван до запуска функции.
                const callback = get().onLoginSuccess
                if (callback) {
                    // Тайм-аут нужен для вывода вызова из потока на случай,
                    // если внутри возникнут проблемы.
                    setTimeout(() => callback(userData), 50)
                }
            }
            set({
                onLoginSuccess: null,
                onRegistrationSuccess: null,
            })
        },

        onNotAuthorizedApiError(): void {
            get().setUserData(null)
            redirectOnLogout(useAppRootStore.getState().section, true)
        },

        async reloadUserData(
            silent: boolean = false,
            preferCached: boolean = false,
            requestConfig?: RequestInit
            // Тут нельзя использовать AbortController!
            // Из-за отмены запроса возникают сложно отлавливаемые побочные эффекты
            // типа перенаправления на страницу /login
            // и неправильные срабатывания onLoginSuccess и onUserDataLoaded.
        ): Promise<UserData | null> {
            if (!silent) {
                set({
                    loadingUserData: true,
                })
            }
            // Вызов заданной функции после загрузки данных пользователя.
            const onUserDataLoaded = (user: UserData | null): void => {
                const callback = get().onUserDataLoaded
                if (callback) {
                    if (user) {
                        set({
                            onLoginSuccess: null,
                            onRegistrationSuccess: null,
                            onUserDataLoaded: null,
                        })
                    } else {
                        set({
                            onUserDataLoaded: null,
                        })
                    }
                    // Тайм-аут нужен для вывода вызова из потока на случай,
                    // если внутри возникнут проблемы.
                    setTimeout(() => callback(user), 50)
                }
            }
            const existingUserData = get().user
            if (preferCached && existingUserData) {
                const cachedUserData: UserData | null = CacheService.get<UserData>('user-data-polling')
                if (cachedUserData) {
                    if (cachedUserData.id === existingUserData.id) {
                        get().setUserData(cachedUserData, true)
                        onUserDataLoaded(cachedUserData)
                        return Promise.resolve(cachedUserData)
                    } else {
                        CacheService.forget('user-data-polling')
                    }
                }
            }
            // Запрос данных пользователя.
            return UserAccountApi.getUserData(undefined, requestConfig)
                .then((user: UserData) => {
                    // Данные загружены успешно.
                    get().setUserData(user, true)
                    onUserDataLoaded(user)
                    CacheService.set(
                        'user-data-polling',
                        user,
                        Math.ceil((AppConfig.userInfoPollingInterval * 0.8) / 60000)
                    )
                    return user
                })
                .catch((error: ApiError) => {
                    // Не удалось загрузить данные пользователя.
                    set({
                        loadingUserData: false,
                    })
                    onUserDataLoaded(null)
                    throw error
                })

        },

        logout(section?: AppSection): void {
            // Тут нам не нужно вызывать setIsLoading(true)
            const rootStore = useAppRootStore.getState()
            const backup = get().user
            AnalyticsEventReporter.userLoggedOut()
            get().setUserData(null, true)
            CacheService.forget('user-data-polling')
            /*
             * Из-за того что у нас токен лежит в HttpOnly=true cookie,
             * удалить его можно только со стороны сервера.
             */
            UserAuthApi.logout()
                .then(() => {
                    setTimeout(() => {
                        redirectOnLogout(section ?? rootStore.section)
                        rootStore.setIsLoading(false)
                    }, 500)
                })
                .catch((error: ApiError) => {
                    get().setUserData(backup, true)
                    handleErrorResponse(error)
                    rootStore.setIsLoading(false)
                })
        },
    })
)

// Перенаправление при логауте.
function redirectOnLogout(
    section?: AppSection,
    // При выходе со страницы ЛК перейти на страницу входа или на стартовую?
    preferLoginPage: boolean = false
): void {
    if (section !== 'public') {
        NavigationService.navigateWithHardReload(
            preferLoginPage ? appUrl().login : appUrl().index
        )
    } else {
        NavigationService.hardReload()
    }
}
